import classNames from "classnames";
import first from "lodash/first";
import has from "lodash/has";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import uniqueId from "lodash/uniqueId";
import {
  CustomValueOption,
  CustomValueSelect
} from "PFApp/components/custom_value_select/custom_value_select";
import { EXPIRABLE_CUSTOM_TYPES } from "PFApp/constants/expirable_custom_types";
import { InlineCalendar } from "PFComponents/calendar/inline_calendar";
import Toggle from "PFComponents/toggle/toggle";
import { allowsNewValues } from "PFCore/helpers/custom_type";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import {
  AutocompleteCustomValuesResponse,
  fetchAutocompleteCustomValues
} from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { fetchLocationsOptions } from "PFCore/services/locations";
import {
  CustomType,
  CustomValue,
  Id,
  Position,
  Profile,
  PureCustomValue,
  PureCustomValueData
} from "PFTypes";
import { CSSProperties, ReactNode, RefObject, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import css from "./custom_values_edit_field.module.scss";
import { useCommonProps } from "./use_common_props";

type ActivityProps =
  | {
      isActivity: true;
      activityCreator: Profile | null;
    }
  | {
      isActivity?: false;
      activityCreator?: never;
    };

export type CustomValuesEditFieldProps = {
  handleChange: (values: PureCustomValueData[]) => void;
  values?: PureCustomValueData[]; // in a legacy code, values can be undefined (for example `basic_info.jsx on profile page)
  customType: CustomType | null;
  kind?: "bordered" | "blank";
  position?: Position;
  profile?: Profile;
  connectedValuesIds?: Id[];
  parseOptions?: (options: CustomValueOption[]) => CustomValueOption[];
  adminPage?: boolean;
  perPage?: number;
  multi?: boolean;
  required?: boolean;
  letClear?: boolean;
  letCreate?: boolean | null;
  tip?: string;
  label?: string;
  title?: string;
  placeholder?: string;
  errors?: string | string[];
  hasValuesWithExpiryDate?: boolean;
  createOptionBlocklist?: string[];
  closeOnChange?: boolean;
  useGlobalValues?: boolean;
  /** When true, no API call is made until the user types at least one character.
   * Use for types with large value sets (e.g. skills). */
  requiresSearch?: boolean;
  disabled?: boolean;
  forceUnlocked?: boolean;
  renderDisplayValues?: (disabled?: boolean) => ReactNode;
  qaIdPrefix?: string;
  classes?: {
    root?: string;
    calendarInput?: string;
  };
  portalRef?: RefObject<HTMLElement | null>;
  onRestore?: VoidFunction;
  restoreLabel?: string;
  displayValuesBelow?: boolean;
  hideLabel?: boolean;
  hideTip?: boolean;
  variant?: "default" | "table";
  dropdownWidth?: number;
  focusSearchInputOnOpen?: boolean;
  tabIndex?: number;
  style?: CSSProperties;
  inputRef?: RefObject<any>;
} & ActivityProps;

export const CustomValuesEditField = ({
  kind,
  position,
  customType,
  errors,
  adminPage,
  profile,
  label,
  title,
  placeholder,
  tip,
  required,
  isActivity,
  activityCreator,
  forceUnlocked,
  perPage = 250,
  multi,
  values = [],
  qaIdPrefix,
  letClear,
  disabled,
  letCreate = null,
  hasValuesWithExpiryDate,
  classes = {},
  parseOptions,
  createOptionBlocklist,
  renderDisplayValues,
  displayValuesBelow,
  handleChange,
  useGlobalValues,
  requiresSearch,
  connectedValuesIds = [],
  portalRef,
  onRestore,
  restoreLabel,
  hideLabel,
  hideTip,
  variant = "default",
  dropdownWidth,
  focusSearchInputOnOpen,
  tabIndex,
  style,
  inputRef
}: CustomValuesEditFieldProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "customValues" });
  const dropdownId = useRef(uniqueId("select-dropdown"));
  const { formatISODate } = useDateFormatter();
  const commonProps = useCommonProps({
    adminPage,
    customType,
    profile,
    position,
    isActivity,
    activityCreator,
    forceUnlocked,
    errors,
    placeholder,
    tip: hideTip ? "" : tip,
    label: hideLabel ? "" : label,
    title,
    kind,
    disabled,
    required,
    variant,
    tabIndex,
    style
  });

  // When the value is not passed we default to custom type config
  const finalLetCreate = isNil(letCreate) && customType ? allowsNewValues(customType) : letCreate;

  const getId = (customValue: PureCustomValue | PureCustomValueData) =>
    customValue.id ?? getAttribute(customValue, "globalId");

  const formatOptions = (response: PureCustomValue[] | AutocompleteCustomValuesResponse) => {
    const formattedEntries = response.map((customValue) => ({
      ...customValue,
      id: getId(customValue),
      value: String(customValue.text || customValue.value),
      original: customValue
    }));
    return {
      entries: formattedEntries,
      meta: { total: formattedEntries.length }
    };
  };

  const fetchOptions = async ({ searchTerm, ids }: { searchTerm: string; ids?: string[] }) => {
    const term = searchTerm;
    if (ids?.length) {
      return await Promise.resolve(formatOptions(values as PureCustomValue[]));
    }
    if (requiresSearch && !term && isEmpty(connectedValuesIds)) {
      return { entries: [], meta: { total: 0 } };
    }
    const type = customType!.name;

    if (customType?.value_type === "location") {
      const result =
        term.length < 3
          ? ([] as PureCustomValue[])
          : (await fetchLocationsOptions({ term, customTypeId: customType.id })).entries;
      return formatOptions(result);
    }

    const result = await fetchAutocompleteCustomValues({
      term,
      type,
      limit: perPage,
      ...(useGlobalValues ? { global: true } : {}),
      ...(!isEmpty(connectedValuesIds) ? { connectedOptions: true, customValueIds: connectedValuesIds } : {})
    });
    return formatOptions(result);
  };

  const isMulti = multi ?? customType?.kind === "multiple";

  const handleBooleanChange = (value: boolean) => {
    handleChange([{ value } as PureCustomValueData]);
  };

  const handleCalendarChange = (date: string) => {
    // backend does not update date value if it is provided with existing id
    // (contrary to other custom values updates)
    handleChange(date ? [{ value: formatISODate(date) } as PureCustomValueData] : []);
  };

  const isCalendar = customType?.value_type === "date";
  const isBoolean = customType?.value_type === "boolean";

  const isChecked = useMemo(() => {
    if (!isBoolean) {
      return false;
    }
    const customValue = (first(values) || {}) as PureCustomValueData;
    // in some cases we receive only "text"
    const value = has(customValue, "value") ? customValue.value : customValue.text;
    return isString(value) ? value === "true" : Boolean(value);
  }, [values, isBoolean]);

  const hasExpiryDate = isNil(hasValuesWithExpiryDate)
    ? EXPIRABLE_CUSTOM_TYPES.includes(customType?.name || "")
    : hasValuesWithExpiryDate;

  const handleExpiryDateChange = (option: CustomValueOption) => {
    const newValues = [...values] as CustomValue[];
    const changedValue = newValues.find((value) => value.id === option.id);
    if (changedValue) {
      changedValue.expiryDate = option.original?.expiryDate;
    }
    handleChange(newValues);
  };

  const handleCreate = (createdOption: CustomValueOption) => {
    const newOption = { ...createdOption, created: true };
    const newValues = (isMulti ? [...values, newOption] : [newOption]) as PureCustomValueData[];
    handleChange(newValues);
    return Promise.resolve(createdOption);
  };

  const commonSelectProps = {
    dropdownId: `${dropdownId.current}-${customType?.name}-${connectedValuesIds}`,
    fetchOptions,
    displayValueBelow: displayValuesBelow,
    withExpiryDate: hasExpiryDate,
    onRestore,
    onDateChange: handleExpiryDateChange,
    onClear: letClear && values.length > 0 ? () => handleChange([]) : undefined,
    onCreateOption: finalLetCreate ? handleCreate : undefined,
    createOptionBlocklist,
    restoreLabel,
    displayValues: renderDisplayValues?.(commonProps.locked || disabled),
    isFetchingSelectedOptions: false,
    parseOptions,
    tooltipAppendTo: portalRef?.current || undefined,
    createButton: {
      ...(customType?.name === "skills" && { tooltipContent: t("createDescription.skills") })
    },
    portalRef,
    variant,
    dropdownWidth,
    focusSearchInputOnOpen,
    inputRef
  };

  return (
    <div
      className={classNames(classes.root, { [css.tableVariant]: variant === "table" })}
      key={customType?.id}
      data-qa-id={`${qaIdPrefix}-${customType?.name}`}
    >
      {isBoolean ? (
        <Toggle
          {...commonProps}
          checked={isChecked}
          onChange={handleBooleanChange}
          qaId={`${qaIdPrefix}-${customType?.name}`}
          label={label}
          switchRef={inputRef}
        />
      ) : isCalendar ? (
        <InlineCalendar
          {...commonProps}
          selectedDate={(first(values)?.value as string) ?? null}
          handleChange={handleCalendarChange}
          portalRef={portalRef}
          letClear={letClear}
          inputRef={inputRef}
          inputClassName={classes.calendarInput}
        />
      ) : (
        <>
          {isMulti ? (
            <CustomValueSelect
              multiple
              value={values.map(getId)}
              onChange={(values: CustomValueOption[]) => handleChange(values.map(({ original }) => original))}
              {...commonSelectProps}
              {...commonProps}
            />
          ) : (
            <CustomValueSelect
              value={values[0] && getId(values[0])}
              onChange={(value: CustomValueOption) => handleChange([value.original])}
              {...commonSelectProps}
              {...commonProps}
            />
          )}
        </>
      )}
    </div>
  );
};
